import { useMemo } from 'react';
import { Button, Col, Flex, Form, Input, InputNumber, Radio, Row, Select, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { SelectPlus } from 'Components/SelectPlus';
import { InputCurrency } from '@/shared/components/inputs/InputCurrency';
import { SelectMunicipality } from '@features/locations/components/SelectMunicipality';
import { SelectDepartment } from '@features/locations/components/SelectDepartment';
import { SelectBrand } from '@features/vehicles/components/VehicleForm/SelectBrand';
import { OPERATORS_BY_FIELD_TYPE, RULE_OPERATORS } from '../constants';

const { Text } = Typography;

// Validación del valor según operador y tipo (solo para mostrar error cuando showFieldErrors)
const getValueError = (rule, fieldType) => {
  if (rule.operator === 'between') {
    if (!Array.isArray(rule.value) || rule.value.length !== 2) return 'Debe indicar mínimo y máximo';
    if (rule.value[0] == null || rule.value[1] == null) return 'Valor requerido';
    return null;
  }
  if (rule.operator === 'in' || rule.operator === 'not_in') {
    if (!Array.isArray(rule.value) || rule.value.length === 0) return 'Selecciona al menos un valor';
    return null;
  }
  if (rule.value === undefined || rule.value === null || rule.value === '') return 'Valor requerido';
  return null;
};

export const RuleFormRow = ({ rule, ruleFields, onRemove, onChange, showFieldErrors = false }) => {
  const selectedField = ruleFields.find((field) => field.id === rule.field);
  const fieldType = selectedField?.fieldType;
  const availableOperators = useMemo(() => {
    if (!fieldType) return [];
    return OPERATORS_BY_FIELD_TYPE[fieldType] || [];
  }, [fieldType]);

  const isBetweenOperator = rule.operator === 'between';
  const isInOperator = rule.operator === 'in' || rule.operator === 'not_in';
  const isMultipleValueOperator = isBetweenOperator || isInOperator;

  const valueError = fieldType && rule.operator ? getValueError(rule, fieldType) : null;

  const handleFieldChange = (fieldId) => {
    const newField = ruleFields.find((f) => f.id === fieldId);
    if (!newField) return;

    // Resetear operador y valor cuando cambia el campo
    const newFieldType = newField.fieldType;
    const newAvailableOperators = OPERATORS_BY_FIELD_TYPE[newFieldType] || [];
    const defaultOperator = newAvailableOperators[0]?.value;

    onChange({
      ...rule,
      field: fieldId,
      operator: defaultOperator,
      value: undefined,
    });
  };

  const handleOperatorChange = (operator) => {
    // Resetear valor cuando cambia el operador
    onChange({
      ...rule,
      operator,
      value: undefined,
    });
  };

  const handleValueChange = (value) => {
    onChange({
      ...rule,
      value,
    });
  };

  const renderValueInput = () => {
    if (!fieldType || !rule.operator) return null;

    // Operador "between" - dos inputs para min y max
    if (isBetweenOperator) {
      const currentValue = Array.isArray(rule.value) ? rule.value : [undefined, undefined];

      if (fieldType === 'integer' || fieldType === 'currency') {
        const InputComponent = fieldType === 'currency' ? InputCurrency : InputNumber;

        return (
          <Flex gap='small' style={{ width: '100%' }}>
            <Form.Item
              style={{ flex: 1, marginBottom: 0 }}
              validateStatus={showFieldErrors && rule.value && !Array.isArray(rule.value) ? 'error' : ''}
              help={showFieldErrors && rule.value && !Array.isArray(rule.value) ? 'Debe ser un rango [min, max]' : ''}
            >
              <InputComponent
                placeholder='Mínimo'
                value={currentValue[0]}
                onChange={(val) => {
                  handleValueChange([val, currentValue[1]]);
                }}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              style={{ flex: 1, marginBottom: 0 }}
              validateStatus={showFieldErrors && rule.value && !Array.isArray(rule.value) ? 'error' : ''}
            >
              <InputComponent
                placeholder='Máximo'
                value={currentValue[1]}
                onChange={(val) => {
                  handleValueChange([currentValue[0], val]);
                }}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Flex>
        );
      }
    }

    // Campos que manejan su propio Select
    const fieldCode = selectedField?.code;
    const selectMode = isInOperator ? 'multiple' : 'single';
    const selectValue = isInOperator ? (Array.isArray(rule.value) ? rule.value : []) : rule.value;

    if (fieldCode === 'vehicle.circulation_municipality_code') {
      return (
        <SelectMunicipality
          mode={selectMode}
          valueKey='code'
          value={selectValue}
          onChange={handleValueChange}
          placeholder={selectMode === 'single' ? 'Seleccionar ciudad' : 'Seleccionar ciudades'}
        />
      );
    }
    if (fieldCode === 'vehicle.circulation_department_code') {
      return (
        <SelectDepartment
          mode={selectMode}
          valueKey='code'
          value={selectValue}
          onChange={handleValueChange}
        />
      );
    }
    if (fieldCode === 'vehicle.brand') {
      return (
        <SelectBrand
          mode={selectMode}
          value={selectValue}
          onChange={handleValueChange}
        />
      );
    }

    // Operadores "in" y "not_in" - select múltiple
    if (isInOperator) {
      const options = selectedField?.options || [];
      const currentValue = Array.isArray(rule.value) ? rule.value : [];

      return (
        <SelectPlus
          mode='multiple'
          value={currentValue}
          onChange={handleValueChange}
          options={options}
          placeholder='Selecciona valores'
          style={{ width: '100%' }}
        />
      );
    }

    // Campos boolean - Radio Group
    if (fieldType === 'boolean') {
      return (
        <Radio.Group
          value={rule.value}
          onChange={(e) => handleValueChange(e.target.value)}
        >
          <Space>
            <Radio value={true}>Sí</Radio>
            <Radio value={false}>No</Radio>
          </Space>
        </Radio.Group>
      );
    }

    // Campo select - SelectPlus simple
    if (fieldType === 'select') {
      const options = selectedField?.options || [];
      return (
        <SelectPlus
          mode='single'
          value={rule.value}
          onChange={handleValueChange}
          options={options}
          placeholder='Selecciona un valor'
          style={{ width: '100%' }}
        />
      );
    }

    // Campo string - Input
    if (fieldType === 'string') {
      return (
        <Input
          value={rule.value}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder='Ingresa el valor'
        />
      );
    }

    // Campo integer - InputNumber
    if (fieldType === 'integer') {
      return (
        <InputNumber
          value={rule.value}
          onChange={handleValueChange}
          placeholder='Ingresa el valor'
          style={{ width: '100%' }}
        />
      );
    }

    // Campo currency - InputCurrency
    if (fieldType === 'currency') {
      return (
        <InputCurrency
          value={rule.value}
          onChange={handleValueChange}
          placeholder='Ingresa el valor'
          style={{ width: '100%' }}
        />
      );
    }

    return null;
  };

  return (
    <Row gutter={[8, 0]} align='middle' style={{ width: '100%', marginBottom: 8 }}>
      <Col span={8}>
        <Form.Item
          style={{ marginBottom: 0 }}
          validateStatus={showFieldErrors && !rule.field ? 'error' : ''}
          help={showFieldErrors && !rule.field ? 'Campo requerido' : ''}
        >
          <SelectPlus
            placeholder='Selecciona un campo'
            value={rule.field}
            onChange={handleFieldChange}
            options={ruleFields.map((field) => ({
              value: field.id,
              label: field.label,
            }))}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Col>

      <Col span={6}>
        <Form.Item
          style={{ marginBottom: 0 }}
          validateStatus={showFieldErrors && !rule.operator ? 'error' : ''}
          help={showFieldErrors && !rule.operator ? 'Operador requerido' : ''}
        >
          <SelectPlus
            placeholder='Operador'
            value={rule.operator}
            onChange={handleOperatorChange}
            options={availableOperators.map((op) => ({
              value: op.value,
              label: op.label,
            }))}
            disabled={!rule.field}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item
          style={{ marginBottom: 0 }}
          validateStatus={showFieldErrors && valueError ? 'error' : ''}
          help={showFieldErrors && valueError ? valueError : ''}
        >
          {renderValueInput()}
        </Form.Item>
      </Col>

      <Col span={2}>
        <Button
          type='text'
          danger
          icon={<DeleteOutlined />}
          onClick={onRemove}
          style={{ width: '100%' }}
        />
      </Col>
    </Row>
  );
};
